import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { Header } from "./IndustriesPanel";

export default function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", index: "", tagline: "", summary: "" });
  const site = useSite();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/admin/categories", {
        ...form,
        index: form.index || String(items.length + 1).padStart(2, "0"),
        order: items.length + 1,
      });
      setForm({ name: "", index: "", tagline: "", summary: "" });
      await load();
      await site.reload();
      toast.success("Category added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async (id, patch) => {
    try {
      await api.patch(`/admin/categories/${id}`, patch);
      await load();
      await site.reload();
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category? It must have no features.")) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      await load();
      await site.reload();
      toast.success("Category deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="categories-panel" className="space-y-6">
      <Header title="Categories" subtitle="Top-level groupings of features (e.g. Banking, Workplace Safety)." />

      <form onSubmit={add} className="bg-white border border-neutral-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4">
          <Label>Name *</Label>
          <Input data-testid="category-input-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Aviation Security" />
        </div>
        <div className="md:col-span-2">
          <Label>Index</Label>
          <Input data-testid="category-input-index" value={form.index} onChange={(e) => setForm((f) => ({ ...f, index: e.target.value }))} placeholder="08" />
        </div>
        <div className="md:col-span-6">
          <Label>Tagline</Label>
          <Input data-testid="category-input-tagline" value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} placeholder="Short, punchy subhead." />
        </div>
        <div className="md:col-span-12">
          <Label>Summary</Label>
          <Textarea data-testid="category-input-summary" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} rows={2} placeholder="1–2 sentence summary shown above feature cards." />
        </div>
        <div className="md:col-span-12 flex justify-end">
          <Button type="submit" data-testid="category-add" className="bg-brand text-white hover:bg-brand-dark font-semibold">
            Add category
          </Button>
        </div>
      </form>

      <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
        {loading && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
        {items.map((it) => (
          <CatRow key={it.id} item={it} onSave={save} onRemove={remove} />
        ))}
      </div>
    </div>
  );
}

function CatRow({ item, onSave, onRemove }) {
  const [val, setVal] = useState(item);
  const dirty = ["name", "index", "tagline", "summary"].some((k) => (val[k] || "") !== (item[k] || ""));
  return (
    <div className="p-4" data-testid={`category-row-${item.slug}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4">
          <Label>Name</Label>
          <Input value={val.name} onChange={(e) => setVal({ ...val, name: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <Label>Index</Label>
          <Input value={val.index} onChange={(e) => setVal({ ...val, index: e.target.value })} />
        </div>
        <div className="md:col-span-6">
          <Label>Tagline</Label>
          <Input value={val.tagline || ""} onChange={(e) => setVal({ ...val, tagline: e.target.value })} />
        </div>
        <div className="md:col-span-12">
          <Label>Summary</Label>
          <Textarea value={val.summary || ""} onChange={(e) => setVal({ ...val, summary: e.target.value })} rows={2} />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          {item.slug}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
          >
            Delete
          </button>
          <button
            type="button"
            disabled={!dirty}
            onClick={() => onSave(item.id, { name: val.name, index: val.index, tagline: val.tagline, summary: val.summary })}
            className="text-xs font-semibold text-white px-3 py-1.5 rounded-md bg-brand disabled:bg-neutral-100 disabled:text-neutral-400"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
    {children}
  </span>
);
